/**
 * Drafts list for the Order Queue's "Drafts (N)" button (Phase 104, D-22).
 *
 * Drafts live on the server, so a teammate can finish one. The list is read
 * only while the dialog is open, always for the queue's location (carry-over
 * 13: the API also allows an org-wide list; this surface never asks for it).
 * Each row: who it is for, the job, when and by whom it was last saved, Open
 * (the create page loads it from ?draft=<id>) and Delete. Delete asks first,
 * inline in the row: a second dialog over this one is an anti-pattern
 * (DESIGN.md §10).
 *
 * Errors show the server's message (carry-over 1: causes are dropped on the
 * wire, the message is what reaches the browser).
 *
 * Focus follows the row: Delete moves it to Keep (the safe answer), Keep back
 * to that row's Delete, and once a draft is gone to the next row's Delete
 * (else the previous row's, else the dialog itself).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { AlertTriangle, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { trpc } from '@/lib/trpc';
import { parseDateValue, useDateFnsLocale } from '@/lib/format-dates';

interface DraftsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  locationId: string;
}

export function DraftsDialog({ open, onOpenChange, locationId }: DraftsDialogProps) {
  const { t } = useTranslation('production');
  const locale = useDateFnsLocale();
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  // Where focus goes after the next render: a button by selector (null = the
  // dialog), optionally only once a deleted row has left the list.
  const [focusRequest, setFocusRequest] = useState<{ selector: string | null; afterGone?: string } | null>(null);

  const drafts = trpc.manualOrders.drafts.list.useQuery(
    { location_id: locationId },
    { enabled: open && !!locationId },
  );
  // Names for "by whom". The same people who can see drafts (orders.create:
  // Super Admin, Admin, Warehouse Coordinator) can read the team list. If it
  // cannot be read, rows still show when they were saved.
  const users = trpc.users.list.useQuery(undefined, {
    enabled: open,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const nameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of users.data ?? []) {
      const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim() || u.email;
      if (u.id && name) map.set(u.id, name);
    }
    return map;
  }, [users.data]);

  const rows = useMemo(() => drafts.data ?? [], [drafts.data]);

  const remove = trpc.manualOrders.drafts.remove.useMutation({
    onSettled: () => {
      // Deleted, or already gone (NOT_FOUND): either way the list and the
      // queue's Drafts (N) must stop showing it.
      void utils.manualOrders.drafts.list.invalidate();
      void utils.manualOrders.drafts.count.invalidate();
      void utils.manualOrders.queue.badges.invalidate();
    },
  });

  const deleteSelector = (id: string) => `[data-draft-delete="${id}"]`;

  const handleDelete = (id: string) => {
    remove.mutate(
      { id },
      {
        onSuccess: () => {
          const index = rows.findIndex((row) => row.id === id);
          const neighbour = rows[index + 1] ?? rows[index - 1] ?? null;
          setConfirmingId(null);
          setFocusRequest({ selector: neighbour ? deleteSelector(neighbour.id) : null, afterGone: id });
          toast.success(t('orders.manual.draftsDialog.deleted'));
        },
        onError: (error) => {
          setConfirmingId(null);
          setFocusRequest({ selector: deleteSelector(id) });
          toast.error(error.message || t('orders.manual.draftsDialog.deleteFailed'), {
            duration: Infinity,
          });
        },
      },
    );
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) setConfirmingId(null);
    onOpenChange(next);
  };

  useEffect(() => {
    if (!focusRequest) return;
    const root = contentRef.current;
    if (!root) return;
    if (focusRequest.afterGone && rows.some((row) => row.id === focusRequest.afterGone)) return;
    const target = focusRequest.selector ? root.querySelector<HTMLElement>(focusRequest.selector) : null;
    (target ?? root).focus();
    setFocusRequest(null);
  }, [focusRequest, rows]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent ref={contentRef} className="max-h-[85vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{t('orders.manual.draftsDialog.title')}</DialogTitle>
          <DialogDescription>{t('orders.manual.draftsDialog.description')}</DialogDescription>
        </DialogHeader>

        {drafts.isLoading ? (
          <div className="space-y-2" aria-hidden="true">
            {[1, 2].map((i) => (
              <Skeleton key={i} className="h-[68px] w-full rounded-md" />
            ))}
          </div>
        ) : drafts.isError ? (
          <div className="flex flex-col items-center gap-2 py-8 text-center" role="alert">
            <AlertTriangle className="h-8 w-8 text-destructive" aria-hidden="true" />
            <p className="font-medium">{t('orders.manual.draftsDialog.failedToLoad')}</p>
            <Button variant="outline" size="sm" onClick={() => void drafts.refetch()}>
              {t('orders.manual.draftsDialog.retry')}
            </Button>
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center gap-1 py-8 text-center">
            <FileText className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
            <p className="font-medium">{t('orders.manual.draftsDialog.empty')}</p>
            <p className="text-sm text-muted-foreground">{t('orders.manual.draftsDialog.emptyHint')}</p>
          </div>
        ) : (
          <ul className="divide-y rounded-md border" aria-label={t('orders.manual.draftsDialog.listLabel')}>
            {rows.map((d) => {
              const title =
                d.customerCompany?.trim() || d.customerName?.trim() || t('orders.manual.draftsDialog.untitled');
              const hasCustomer = !!(d.customerCompany?.trim() || d.customerName?.trim());
              const detail = [d.customerCompany?.trim() ? d.customerName?.trim() : null, d.jobName?.trim()]
                .filter((part): part is string => !!part)
                .join(' · ');
              const savedDate = parseDateValue(d.updatedAt);
              const when = savedDate ? formatDistanceToNow(savedDate, { addSuffix: true, locale }) : '';
              const who = d.updatedBy ? nameById.get(d.updatedBy) : undefined;
              const isConfirming = confirmingId === d.id;
              const isDeleting = remove.isPending && remove.variables?.id === d.id;
              return (
                <li key={d.id} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 p-3">
                  <div className="min-w-0 flex-1 basis-56">
                    <p
                      className={
                        hasCustomer
                          ? 'truncate font-medium text-foreground'
                          : 'truncate font-medium italic text-muted-foreground'
                      }
                    >
                      {title}
                    </p>
                    {detail && <p className="truncate text-sm text-muted-foreground">{detail}</p>}
                    {when && (
                      <p className="text-xs text-muted-foreground">
                        {who
                          ? t('orders.manual.draftsDialog.savedBy', { when, who })
                          : t('orders.manual.draftsDialog.savedAt', { when })}
                      </p>
                    )}
                  </div>
                  {isConfirming ? (
                    <div className="flex flex-wrap items-center justify-end gap-2" role="group">
                      <span className="text-sm font-medium text-foreground">
                        {t('orders.manual.draftsDialog.confirmDelete')}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        data-draft-keep={d.id}
                        onClick={() => {
                          setConfirmingId(null);
                          setFocusRequest({ selector: deleteSelector(d.id) });
                        }}
                        disabled={isDeleting}
                      >
                        {t('orders.manual.draftsDialog.keep')}
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDelete(d.id)}
                        disabled={isDeleting}
                        aria-busy={isDeleting}
                      >
                        {isDeleting && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
                        {isDeleting
                          ? t('orders.manual.draftsDialog.deleting')
                          : t('orders.manual.draftsDialog.confirmDeleteYes')}
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                        data-draft-delete={d.id}
                        onClick={() => {
                          setConfirmingId(d.id);
                          setFocusRequest({ selector: `[data-draft-keep="${d.id}"]` });
                        }}
                        aria-label={t('orders.manual.draftsDialog.deleteLabel', { name: title })}
                      >
                        {t('orders.manual.draftsDialog.delete')}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/production/orders/new?draft=${encodeURIComponent(d.id)}`)}
                        aria-label={t('orders.manual.draftsDialog.openLabel', { name: title })}
                      >
                        {t('orders.manual.draftsDialog.open')}
                      </Button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
